import { useEffect, useMemo, useRef, useState } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import {
  Cancel01Icon,
  Image01Icon,
  MusicNote01Icon,
  Upload04Icon,
  Video01Icon,
} from '@hugeicons/react'
import clsx from 'clsx'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/shared/ui/Button'
import { Card } from '@/shared/ui/Card'

// ── Media types (form-only concept) ──────────────────────────────────
// `image` is mapped to backend post_type `text` + media_url at the page
// boundary — the backend enum only knows text | video | audio.
const MEDIA_TYPES = [
  { value: 'image', label: 'Imagem', Icon: Image01Icon, accept: 'image/*' },
  { value: 'video', label: 'Vídeo', Icon: Video01Icon, accept: 'video/*' },
  { value: 'audio', label: 'Áudio', Icon: MusicNote01Icon, accept: 'audio/*' },
]

const getMediaType = (value) =>
  MEDIA_TYPES.find((t) => t.value === value) ?? MEDIA_TYPES[0]

// ── Schema ───────────────────────────────────────────────────────────
const postSchema = z.object({
  content: z.string().trim().min(1, 'Escreva algo para publicar'),
  media_type: z.enum(['image', 'video', 'audio']),
  media: z.instanceof(File).optional(),
})

// ── Media type selector ──────────────────────────────────────────────
const MediaTypeToggle = ({ value, onChange }) => (
  <div className="grid grid-cols-3 gap-2">
    {MEDIA_TYPES.map(({ value: v, label, Icon }) => {
      const active = value === v
      return (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          aria-pressed={active}
          className={clsx(
            'flex h-9 items-center justify-center gap-1.5 rounded-xl border text-sm transition-colors',
            active
              ? 'border-primary bg-primary/10 font-medium text-primary'
              : 'border-input bg-transparent text-muted-foreground hover:bg-muted/50'
          )}
        >
          <Icon size={16} variant="stroke" />
          {label}
        </button>
      )
    })}
  </div>
)

// ── Media uploader ───────────────────────────────────────────────────
const MediaUploader = ({ mediaType, value, onChange, error }) => {
  const inputRef = useRef(null)
  const [dragOver, setDragOver] = useState(false)
  const { label, accept } = getMediaType(mediaType)
  const isImage = mediaType === 'image'

  // Object URL for the image preview — revoked on change/unmount.
  const previewUrl = useMemo(
    () => (isImage && value ? URL.createObjectURL(value) : null),
    [isImage, value]
  )
  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    },
    [previewUrl]
  )

  const pickFile = (files) => {
    const file = files?.[0]
    if (file) onChange(file)
  }

  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs text-muted-foreground">
        {label} <span className="font-normal">(opcional)</span>
      </Label>

      {value ? (
        <div className="flex items-center gap-3 rounded-xl border border-input bg-muted/30 p-2">
          {isImage && previewUrl ? (
            <img
              src={previewUrl}
              alt=""
              className="h-14 w-14 shrink-0 rounded-lg object-cover"
            />
          ) : (
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
              {mediaType === 'audio' ? (
                <MusicNote01Icon size={20} variant="stroke" />
              ) : (
                <Video01Icon size={20} variant="stroke" />
              )}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-foreground">
              {value.name}
            </p>
            <p className="text-xs text-muted-foreground">
              {(value.size / 1024 / 1024).toFixed(1)} MB
            </p>
          </div>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="Remover arquivo"
          >
            <Cancel01Icon size={14} variant="stroke" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault()
            setDragOver(true)
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragOver(false)
            pickFile(e.dataTransfer.files)
          }}
          className={clsx(
            'flex h-24 w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed transition-colors',
            dragOver
              ? 'border-primary bg-primary/5'
              : 'border-input bg-muted/20 hover:border-primary/50 hover:bg-muted/40',
            error && 'border-destructive'
          )}
        >
          <Upload04Icon
            size={20}
            variant="stroke"
            className="text-muted-foreground"
          />
          <span className="text-sm text-muted-foreground">
            Arraste ou{' '}
            <span className="font-medium text-primary">selecione</span> uma mídia
          </span>
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => pickFile(e.target.files)}
      />

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}

// ── Form ─────────────────────────────────────────────────────────────
export const CreatePostForm = ({ onSubmit, onClose }) => {
  const form = useForm({
    resolver: zodResolver(postSchema),
    defaultValues: {
      content: '',
      media_type: 'image',
      media: undefined,
    },
  })

  const mediaType = form.watch('media_type')
  const media = form.watch('media')

  const handleSubmit = form.handleSubmit(async (values) => {
    await onSubmit(values)
  })

  return (
    <Card>
      <Card.Body className="p-4">
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {/* Conteúdo */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="post-content">O que você quer compartilhar?</Label>
            <Textarea
              id="post-content"
              rows={5}
              placeholder="Escreva um pensamento, dica ou experiência do evento..."
              {...form.register('content')}
            />
            {form.formState.errors.content && (
              <p className="text-sm text-destructive">
                {form.formState.errors.content.message}
              </p>
            )}
          </div>

          {/* Tipo de mídia */}
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs text-muted-foreground">
              Tipo de mídia
            </Label>
            <MediaTypeToggle
              value={mediaType}
              onChange={(v) => {
                form.setValue('media_type', v)
                form.setValue('media', undefined, { shouldValidate: false })
              }}
            />
          </div>

          {/* Upload de mídia */}
          <MediaUploader
            mediaType={mediaType}
            value={media ?? null}
            onChange={(file) =>
              form.setValue('media', file ?? undefined, {
                shouldValidate: true,
              })
            }
            error={form.formState.errors.media?.message}
          />

          {/* Ações */}
          <div className="flex items-center justify-end gap-2 border-t border-border pt-3">
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              loading={form.formState.isSubmitting}
            >
              Publicar
            </Button>
          </div>
        </form>
      </Card.Body>
    </Card>
  )
}
